<template>
  <div class="bg-card bg-white border-card-border md:px-6">
    <div class="w-full mt-5">
      <div>
        <h1 slot="header" class="text-xl font-semibold pb-2">Tell us about yourself</h1>
        <h3 slot="header" class="pb-3 border-b mb-4">
          Please provide the following information to help us confirm your identity.
        </h3>
      </div>

      <form autocomplete="off" class="w-full pt-3 mt-4" novalidate @submit.prevent="next">
        <Input
          v-for="item in fieldOrder1"
          v-bind:key="item"
          v-model="personalInformation[item].value"
          :id="personalInformation[item].id"
          @address="setAddressComponents"
          :error="personalInformation[item].error"
          :getter="$getters[item]"
          :maxlength="personalInformation[item].maxlength"
          :name="personalInformation[item].name"
          :placeholder="personalInformation[item].placeholder"
          :prefix="['home_phone_number'].includes(item) ? '+1' : ''"
          :readonly="phone_verification_in_progress"
          :setter="$setters[item]"
          :type="['home_phone_number'].includes(item) ? 'tel' : 'text'"
          :underlined="true"
          class="mb-3"
        />

        <div class="mb-6">
          <DatePicker
            v-model="personalInformation['birthdate'].value"
            :name="personalInformation['birthdate'].name"
            :id="personalInformation['birthdate'].id"
            :error="personalInformation['birthdate'].error"
            :disabledDates="{
              from: this.getFromDate(),
              to: this.getToDate()
            }"
            @selectedDate="this.setBirthdate"
            format="MM/dd/yyyy"
          />
        </div>

        <Input
          v-model="personalInformation['ssn'].value"
          :id="personalInformation['ssn'].id"
          :error="personalInformation['ssn'].error"
          :getter="$getters['ssn']"
          :maxlength="personalInformation['ssn'].maxlength"
          :name="personalInformation['ssn'].name"
          :placeholder="personalInformation['ssn'].placeholder"
          :readonly="phone_verification_in_progress"
          :setter="$setters['ssn']"
          :type="'text'"
          :underlined="true"
          class="mb-3"
        />

        <div v-if="notFoundClicked === false" class="relative">
          <div class="flex justify-center">
            <Input
              v-model="personalInformation['address'].value"
              :id="personalInformation['address'].id"
              @address="setAddressComponents"
              :error="personalInformation['address'].error"
              :getter="$getters['address']"
              :maxlength="personalInformation['address'].maxlength"
              :name="personalInformation['address'].name"
              :placeholder="personalInformation['address'].placeholder"
              :setter="$setters['address']"
              :underlined="true"
              class="w-full mt-2"
            />
            <div
              class="invisible md:visible z-20 mr-6 absolute shadow border right-0 rounded absolute focus:outline-none md:w-15"
              style="margin-right: 0px"
            >
              <href
                class="z-30 bg-white block px-3 py-2 h-10 w-full text-center font-semibold text-gray-700 focus:outline-none"
                type="button"
                @click="notFoundClicked = true"
              >
                Cannot find your address 🔎 ?
              </href>
            </div>
          </div>
          <div :class="`visible md:invisible text-black text-xs rounded font-semibold z-1`">
            <a href="#" @click="notFoundClicked = true" class="underline mt-0"
              >Cannot find your address?</a
            >
          </div>
        </div>
        <div v-if="notFoundClicked === true">
          <Input
            v-for="item in fieldOrder3"
            v-bind:key="item"
            v-model="personalInformation[item].value"
            :id="personalInformation[item].id"
            @address="setAddressComponents"
            :error="personalInformation[item].error"
            :getter="$getters[item]"
            :maxlength="personalInformation[item].maxlength"
            :name="personalInformation[item].name"
            :placeholder="personalInformation[item].placeholder"
            :readonly="phone_verification_in_progress"
            :setter="$setters[item]"
            :underlined="true"
            class="mb-3"
          />
          <Dropdown
            v-model="personalInformation.addrStateAbbr.value"
            v-bind:dropdownOptions="this.$store.getters.allStates"
            :value="personalInformation.addrStateAbbr.value"
            :error="personalInformation.addrStateAbbr.error"
            :placeholder="personalInformation.addrStateAbbr.placeholder"
            :name="personalInformation.addrStateAbbr.name"
            :readonly="phone_verification_in_progress"
            :id="personalInformation.addrStateAbbr.id"
            type="text"
            :underlined="true"
            class="mb-3"
          />
        </div>
        <Input
          v-for="item in fieldOrder2"
          v-bind:key="item"
          v-model="personalInformation[item].value"
          :id="personalInformation[item].id"
          @address="setAddressComponents"
          :error="personalInformation[item].error"
          :getter="$getters[item]"
          :maxlength="personalInformation[item].maxlength"
          :name="personalInformation[item].name"
          :placeholder="personalInformation[item].placeholder"
          :prefix="['home_phone_number'].includes(item) ? '+1' : ''"
          :readonly="phone_verification_in_progress"
          :setter="$setters[item]"
          :type="['home_phone_number'].includes(item) ? 'tel' : 'text'"
          :underlined="true"
          class="mb-3"
        />

        <Dropdown
          v-model="personalInformation.how_you_heard.value"
          v-bind:dropdownOptions="howYouHeardOptions"
          :value="personalInformation.how_you_heard.value"
          :error="personalInformation.how_you_heard.error"
          :placeholder="personalInformation.how_you_heard.placeholder"
          :name="personalInformation.how_you_heard.name"
          :readonly="phone_verification_in_progress"
          :id="personalInformation.how_you_heard.id"
          type="text"
          :underlined="true"
          class="mb-3"
        />
        <Input
          v-model="personalInformation['phone_number'].value"
          :error="personalInformation['phone_number'].error"
          :getter="$getters['phone_number']"
          :maxlength="personalInformation['phone_number'].maxlength"
          :name="personalInformation['phone_number'].name"
          :placeholder="personalInformation['phone_number'].placeholder"
          prefix="+1"
          :id="personalInformation.how_you_heard.id"
          :readonly="phone_verification_in_progress"
          :setter="$setters['phone_number']"
          type="tel"
          :underlined="true"
          :b="
            phoneVerified
              ? 'A security code will be texted to this number when you click Next for verification purposes'
              : ''
          "
          class="mb-3"
        />
        <Input
          v-if="phone_verification_in_progress"
          v-model="code"
          type="text"
          class="mb-2"
          maxlength="6"
          name="Verification Code"
          :b="`A verification code was texted to +1 ${$getters.phone_number(
            personalInformation.phone_number.value
          )}`"
          placeholder="123456"
          :underlined="true"
          :error="phoneVerificationError"
          @keydown.enter.native="handleEnter($event)"
        />
        <button
          type="button"
          v-if="phone_verification_in_progress"
          @click="togglePhoneVerification()"
          aria-label="verify"
          class="focus:outline-none text-blue-500 hover:text-blue-400 mb-4 text-sm"
        >
          Edit Phone Number
        </button>
      </form>

      <!-- Navigation -->
      <div class="flex w-full mt-4">
        <button
          class="hover:text-primary mr-auto focus:outline-none rounded"
          type="btn"
          aria-label="navigate back"
          v-on:click="handleBack()"
        >
          <i
            class="fas fa-arrow-left bg-gray-300 rounded-full flex items-center text-lg justify-center w-8 h-8"
          ></i>
        </button>

        <base-button
          type="primary ml-auto"
          :disabled="!nextEnabled"
          :loading="nextLoading"
          aria-label="navigate next"
          @click="handleNext()"
        >
          Next
        </base-button>
      </div>
      <transition name="in--up">
        <div v-if="!nextEnabled" class="flex items-center justify-center w-full px-4">
          <h3 class="text-red-500 text-sm">Please fix all errors before proceeding.</h3>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Engine from '@/mixins/Engine'
import Helper from '@/mixins/Helper'

import Dropdown from '@/components/VIVA/Dropdown.vue'

export default {
  name: 'PersonalInformation',
  mixins: [Engine, Helper],
  props: {
    initialData: Object
  },
  components: {
    Dropdown
  },
  data: function () {
    return {
      phoneVerificationError: '',
      notFoundClicked: false,
      personalInformation: {
        given_name: {
          name: 'First name',
          placeholder: 'Jane',
          value: '',
          error: '',
          id: 'first_name'
        },
        middle_name: {
          name: 'Middle name (optional)',
          placeholder: 'Joy',
          optional: true,
          value: '',
          error: '',
          id: 'middle_name'
        },
        family_name: {
          name: 'Last name',
          placeholder: 'Doe',
          value: '',
          error: '',
          id: 'last_name'
        },
        birthdate: {
          name: 'Date of Birth',
          value: '',
          error: '',
          id: 'date_of_birth'
        },
        address: {
          name: 'Address',
          placeholder: 'Please enter your address',
          value: '',
          error: '',
          id: 'address'
        },
        unit: {
          name: 'Address line 2 (optional)',
          placeholder: 'Please enter your apartment/unit number',
          optional: true,
          value: '',
          error: '',
          id: 'address_2'
        },
        personal_email: {
          name: 'Alternate email',
          placeholder: 'vivafinance@gmail.com',
          value: '',
          error: '',
          id: 'alternate_email'
        },
        ssn: {
          name: 'Social security number',
          maxlength: 11,
          placeholder: 'XXX-XX-XXXX',
          value: '',
          error: '',
          id: 'ssn'
        },
        how_you_heard: {
          name: 'How did you hear about VIVA?',
          maxlength: 140,
          placeholder: 'Choose Below',
          value: '',
          error: '',
          id: 'how_did_you_hear'
        },
        referral_code: {
          name: 'Referral/Promo Code (optional)',
          placeholder: 'a1b2c3',
          optional: true,
          value: '',
          error: '',
          id: 'referral_code'
        },
        home_phone_number: {
          name: 'Home phone number (optional)',
          maxlength: 14,
          placeholder: '(XXX) XXX-XXXX',
          optional: true,
          value: '',
          error: '',
          id: 'home_phone_number'
        },
        phone_number: {
          name: 'Cell phone number',
          maxlength: 14,
          placeholder: '(XXX) XXX-XXXX',
          value: '',
          error: '',
          id: 'phone_number'
        },
        addrStreetNumber: {
          name: 'Street number',
          placeholder: '123',
          value: '',
          error: '',
          id: 'street_number'
        },
        addrStreetName: {
          name: 'Street name',
          placeholder: '5th st',
          value: '',
          error: '',
          id: 'street_name'
        },
        addrCity: {
          name: 'City',
          placeholder: 'atlanta',
          value: '',
          error: '',
          id: 'city'
        },
        addrStateAbbr: {
          name: 'State',
          placeholder: 'Choose Below',
          value: '',
          error: '',
          id: 'state'
        },
        addrPostalCode: {
          name: 'Zip code',
          placeholder: '30332',
          value: '',
          error: '',
          id: 'zip_code'
        },
        affiliateId: {
          value: '',
          error: '',
          id: 'affiliate_code',
          optional: true
        }
      },
      addressComponents: {
        // streetNumber: '',
        // streetName: '',
        // city: '',
        // stateAbbr: '',
        // postalCode: ''
      },
      fieldOrder1: ['given_name', 'middle_name', 'family_name'],
      fieldOrder2: ['unit', 'personal_email', 'home_phone_number', 'referral_code'],
      fieldOrder3: ['addrStreetNumber', 'addrStreetName', 'addrCity', 'addrPostalCode'],
      fieldOrder: [
        'given_name',
        'middle_name',
        'family_name',
        'birthdate',
        'ssn',
        'address',
        'unit',
        'personal_email',
        'home_phone_number',
        'referral_code'
      ],
      // ToDo: change back.  Set for testing due to too many request error
      // phone_verified: false,
      phone_verification_in_progress: false,
      phoneChanged: false,
      nextLoading: false,
      code: '',
      firstClick: true,
      howYouHeardOptions: [
        'Email',
        'Co-Worker',
        'Physical Mail',
        'VIVA Representative',
        'Facebook',
        'Google',
        'TikTok',
        'Partner Website',
        'Other'
      ]
    }
  },
  computed: {
    verifiedPhoneNumber() {
      return this.$store.getters.authenticated.phone_number
    },
    // Reflect the value of the fields for the watcher
    birthdate() {
      return this.personalInformation.birthdate.value
    },
    ssn() {
      return this.personalInformation.ssn.value
    },
    address() {
      return this.personalInformation.address.value
    },
    personalEmail() {
      return this.personalInformation.personal_email.value
    },
    homePhone() {
      return this.personalInformation.home_phone_number.value
    },
    phoneNumber() {
      return this.personalInformation.phone_number.value
    },
    firstName() {
      return this.personalInformation.given_name.value
    },
    lastName() {
      return this.personalInformation.family_name.value
    },
    howYouHeard() {
      return this.personalInformation.how_you_heard.value
    },
    streetNum() {
      return this.personalInformation.addrStreetNumber.value
    },
    streetName() {
      return this.personalInformation.addrStreetName.value
    },
    city() {
      return this.personalInformation.addrCity.value
    },
    postalCode() {
      return this.personalInformation.addrPostalCode.value
    },
    state() {
      return this.personalInformation.addrStateAbbr.value
    },
    fieldsValid() {
      const errorsExist = Object.keys(this.personalInformation).filter(
        (fn) => this.personalInformation[fn].error.length > 0
      )
      let emptyRequired = Object.keys(this.personalInformation).filter(
        (fieldName) =>
          !this.personalInformation[fieldName].optional &&
          this.personalInformation[fieldName].value.length === 0
      )
      if (!this.notFoundClicked && emptyRequired.length !== 0) {
        const opt = new Set([
          'addrStreetNumber',
          'addrStreetName',
          'addrCity',
          'addrPostalCode',
          'addrStateAbbr'
        ])
        emptyRequired = Object.values(emptyRequired).filter((name) => {
          return !opt.has(name)
        })
      }
      console.log(`click: ${this.notFoundClicked}`)
      // let emptyRequiredLen = this.notFoundClicked ? emptyRequired.length : emptyRequired.length - 5
      console.log(`number of invalid field ${emptyRequired.length}`)
      return errorsExist.length + emptyRequired.length === 0
    },
    nextEnabled() {
      return this.firstClick || this.fieldsValid
    },
    phoneVerified() {
      return this.$store.getters.phoneVerified || this.reflectTestUser
    },
    reflectTestUser() {
      return (
        (this.$store.getters.isStaging &&
          this.$store.getters.email.endsWith('@vivafinance.email.reflect.run')) ||
        (!this.$store.getters.isProduction && this.phoneNumber.includes('4048675309'))
      )
    }
  },
  methods: {
    handleEnter(event) {
      console.log('e is ', event)
      event.target.blur()
      this.handleNext()
    },

    togglePhoneVerification() {
      this.phone_verification_in_progress = false
    },
    handleNext() {
      if (this.firstClick) {
        this.firstClick = false

        if (!this.notFoundClicked) {
          this.fieldOrder3.map((manualF) => {
            this.personalInformation[manualF].error = ''
          })
          this.personalInformation['addrStateAbbr'].error = ''
        }

        const emptyRequired = Object.keys(this.personalInformation).filter(
          (fieldName) =>
            (!this.personalInformation[fieldName].optional &&
              this.personalInformation[fieldName].value.length === 0 &&
              ![
                'addrStreetNumber',
                'addrStreetName',
                'addrCity',
                'addrStateAbbr',
                'addrPostalCode'
              ].includes(fieldName) &&
              !this.notFoundClicked) ||
            ([
              'addrStreetNumber',
              'addrStreetName',
              'addrCity',
              'addrStateAbbr',
              'addrPostalCode'
            ].includes(fieldName) &&
              this.notFoundClicked &&
              this.personalInformation[fieldName].value.length === 0)
        )

        const existingErrors = Object.keys(this.personalInformation).filter(
          (fieldName) => this.personalInformation[fieldName].error.length > 0
        )

        console.log('emptyRequired: ', emptyRequired)
        console.log('existingErrors: ', existingErrors)
        if (emptyRequired.length + existingErrors.length > 0) {
          emptyRequired.forEach((requiredField) => {
            this.personalInformation[requiredField].error = 'This is a required field.'
          })

          console.log('returning')
          return
        }
        if (
          this.personalInformation.phone_number.error.length > 0 ||
          this.personalInformation.ssn.error.length > 0 ||
          this.personalInformation.home_phone_number.error.length > 0
        ) {
          return
        }
      }
      if (this.phone_verification_in_progress) {
        this.nextLoading = true
        this.$Auth
          .verifyCurrentUserAttributeSubmit('phone_number', this.code)
          .then(() => this.$Auth.currentAuthenticatedUser({ bypassCache: true }))
          .then((user) => {
            console.log(user)
            this.$store.commit('updateUser', user)
            // this.phone_verified = true
            this.phone_verification_in_progress = false
            this.pushGtmData()
            this.uploadAndProgress()
              .then(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                this.$emit('next')
                this.nextLoading = false
              })
              .catch((e) => {
                if (e.message.includes('User with this SSN Already exists')) {
                  this.personalInformation.ssn.error =
                    'User with this SSN Already exists. Please login to the account corresponding to this SSN. If you think this is a mistake, please give us a call at +1 (678) 685-8834..'
                }
                if (e.message.includes('User with this Phone Already exists')) {
                  this.personalInformation.phone_number.error =
                    'User with this Phone Number Already exists. Please login to the account corresponding to this phone. If you think this is a mistake, please give us a call at +1 (678) 685-8834.'
                }
              })
          })
          .catch((e) => {
            this.handleValidationErrors(e)
            console.log(`FE Error in personalInformation/handleNext: ${e}`)

            this.code = ''
            this.phoneVerificationError = 'Wrong verification code'
            this.nextLoading = false
          })
      } else {
        try {
          // Phone Verification not in progress
          const phoneWithExtension = '+1' + this.phoneNumber
          if (
            (this.phoneVerified && !this.phoneChanged) ||
            (this.phoneVerified && this.verifiedPhoneNumber === phoneWithExtension)
          ) {
            this.nextLoading = true
            this.pushGtmData()
            this.uploadAndProgress()
              .then(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                this.$emit('next')
                this.nextLoading = false
              })
              .catch((e) => {
                if (e.message.includes('User with this SSN Already exists')) {
                  this.personalInformation.ssn.error =
                    'User with this SSN Already exists. Please login to the account corresponding to this SSN. If you think this is a mistake, please give us a call at +1 (678) 685-8834.'
                }
                if (e.message.includes('User with this Phone Already exists')) {
                  this.personalInformation.phone_number.error =
                    'User with this Phone Number Already exists. Please login to the account corresponding to this phone. If you think this is a mistake, please give us a call at +1 (678) 685-8834.'
                }
                this.handleValidationErrors(e)
                console.log(e)
                console.log(
                  `FE Error in personalInformation/handleNext - Phone Verification not in progress: ${e}`
                )
                this.error = e
              })
          } else {
            // The phone is not verified, so begin the verification flow
            this.nextLoading = true
            this.phoneChanged = false
            this.uploadAndProgress()
              .then(() => {
                this.$Auth
                  .verifyCurrentUserAttribute('phone_number')
                  .then(() => {
                    this.phone_verification_in_progress = true
                  })
                  .catch((e) => {
                    console.log('phone failed')
                    console.log(e)
                    console.log(
                      `FE Error in personalInformation/handleNext - Phone Verification flow: ${e}`
                    )
                    this.error = e
                  })
                  .finally(() => (this.nextLoading = false))
              })
              .catch((e) => {
                if (e.message.includes('User with this SSN Already exists')) {
                  this.personalInformation.ssn.error =
                    'User with this SSN Already exists. Please log out and login back in again with your cell phone number to continue that application. If you think this is a mistake, please give us a call at +1 (678) 685-8834.'
                }
                if (e.message.includes('User with this Phone Already exists')) {
                  this.personalInformation.phone_number.error =
                    'User with this Phone Number Already exists. Please log out and login back in again with your cell phone number to continue that application. If you think this is a mistake, please give us a call at +1 (678) 685-8834.'
                }
                this.loading = false
                console.log(e)
                console.log(`FE Error in personalInformation/handleNext: ${e}`)
                this.error = e
                this.handleValidationErrors(e)
              })
          }
        } catch (e) {
          this.error = e
          console.log(e)
          console.log(`FE Error in personalInformation/handleNext: ${e}`)
        }
      }
    },
    handleValidationErrors(e) {
      this.nextLoading = false
      this.loading = false
      console.log(e.message)
      if (e.message.includes('home_phone_number')) {
        // Handle home_phone_number error
        this.personalInformation.home_phone_number.error = 'Please enter a valid phone number.'
      }
      if (e.message.includes('phone_number')) {
        // Handle cell_phone_number error
        this.personalInformation.phone_number.error = 'Please enter a valid phone number.'
      }
    },
    setAddressComponents(addrList) {
      this.addressComponents.streetNumber = addrList.find(({ types }) =>
        types.includes('street_number')
      ).long_name
      this.addressComponents.streetName = addrList.find(({ types }) =>
        types.includes('route')
      ).short_name
      this.addressComponents.city = addrList.find(({ types }) =>
        types.includes('locality')
      ).long_name
      this.addressComponents.stateAbbr = addrList.find(({ types }) =>
        types.includes('administrative_area_level_1')
      ).short_name
      this.addressComponents.postalCode = addrList.find(({ types }) =>
        types.includes('postal_code')
      ).long_name
      this.addressComponents.country = addrList.find(({ types }) =>
        types.includes('country')
      ).short_name
      this.addressComponents.lat = addrList.geocode.lat
      this.addressComponents.lng = addrList.geocode.lng
    },
    uploadAndProgress() {
      // return new Promise((resolve) => {
      const outputData = {}
      Object.keys(this.personalInformation).forEach((item) => {
        if (this.personalInformation[item].value && item !== 'birthdate') {
          outputData[item] = this.$setters[item]
            ? this.$setters[item](this.personalInformation[item].value.trim())
            : this.personalInformation[item].value.trim()
        }
        if (item === 'birthdate') {
          outputData['birthdate'] = this.personalInformation[item].value
        }
      })
      outputData.ssn = this.$getters.ssn(this.personalInformation.ssn.value)
      outputData.phone_number = `+1${this.personalInformation.phone_number.value}`
      if (outputData.home_phone_number) {
        outputData.home_phone_number = `+1${outputData.home_phone_number}`
      }

      outputData.birthdate = this.ISODateString(outputData.birthdate)
      outputData.given_name =
        outputData.given_name.slice(0, 1).toUpperCase() + outputData.given_name.slice(1)
      outputData.family_name =
        outputData.family_name.slice(0, 1).toUpperCase() + outputData.family_name.slice(1)
      outputData.addrCountry = 'USA'
      if (this.notFoundClicked) {
        outputData.address = `${outputData.addrStreetNumber} ${outputData.addrStreetName},${outputData.addrCity},${outputData.addrStateAbbr} ${outputData.addrPostalCode},${outputData.addrCountry}`
      }
      console.log('this.addressComponents: ', this.addressComponents)
      if (this.addressComponents.postalCode && this.addressComponents.postalCode.length > 4) {
        outputData.addrCity = this.addressComponents.city
        outputData.addrCountry = this.addressComponents.country
        outputData.addrPostalCode = this.addressComponents.postalCode
        outputData.addrStateAbbr = this.addressComponents.stateAbbr
        outputData.addrStreetName = this.addressComponents.streetName
        outputData.addrStreetNumber = this.addressComponents.streetNumber
        outputData.lng = this.addressComponents.lng
        outputData.lat = this.addressComponents.lat
      } else {
        outputData.lng = -1
        outputData.lat = -1
      }

      outputData.addressComponents = this.addressComponents
      console.log(outputData)
      const personalObject = { d: outputData, accountSettings: false }
      this.$store.commit('applicationData/updatePersonalInformation', outputData)
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch('applicationData/uploadPersonalInformation', personalObject)
          .then(() => resolve())
          .catch((e) => {
            reject(e)
          })
      })
      // this.$emit('updateData', outputData)
      // })
    },
    handleBack() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.$emit('back')
    },
    pushGtmData() {
      window.dataLayer.push({
        event: 'personalInformation',
        stage: this.$store.getters.isProduction ? 'production' : 'dev',
        username: this.$store.getters.username,
        firstName: this.firstName,
        lastName: this.lastName,
        phoneNumber: this.phoneNumber,
        homePhone: this.homePhone,
        personalEmail: this.personalEmail,
        addrCountry: this.addressComponents.country,
        addrPostalCode: this.addressComponents.postalCode
      })
    },
    getFromDate() {
      return this.getDiffYearDate(18) // 18 years old
    },
    getToDate() {
      return this.getDiffYearDate(125) // 125 years
    },
    setBirthdate(v) {
      this.personalInformation['birthdate'].value = v
    }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    // const initialData = {
    //   given_name: 'Alex',
    //   family_name: 'Ketchum',
    //   birthdate: '12/17/1994',
    //   ssn: '111111111',
    //   // address: '265 Ponce de Leon Ave NE Atlanta GA',
    //   personal_email: 'fake@gmail.com',
    //   how_you_heard: 'Email',
    //   phone_number: '+12514243165'
    // }
    const initialData = {}
    Object.keys(this.personalInformation).map((k) => {
      let v = this.$store.state.applicationData[k]
      if (v) initialData[k] = v
    })
    Object.keys(initialData).forEach((k) => {
      if (k.includes('phone_number')) {
        const v = initialData[k]
        if (v.slice(0, 2) === '+1' && v.length === 12) {
          this.personalInformation[k].value = v.slice(2)
        } else if (v[0] === '+' && v.length === 11) {
          this.personalInformation[k].value = v.slice(1)
        } else {
          this.personalInformation[k].value = v
        }
      } else if (k === 'how_you_heard') {
        this.personalInformation[k].value = initialData[k]
      } else if (k === 'birthdate') {
        this.personalInformation.birthdate.value = new Date(initialData[k])
      } else {
        if (this.$setters[k]) {
          this.personalInformation[k].value = this.$setters[k](initialData[k])
        } else {
          this.personalInformation[k].value = initialData[k]
        }
      }
    })
    if (this.$route.query.affiliate) {
      this.personalInformation.affiliateId.value = this.$route.query.affiliate
      this.personalInformation['referral_code'].value = this.$route.query.affiliate
    }

    if (this.$route.query.referral) {
      this.personalInformation['referral_code'].value = this.$route.query.referral
    }
  },

  // set the initial values for the page.  probably from the store.
  watch: {
    ssn: {
      handler(v) {
        if (!v.match(/^\d{9}$/)) {
          this.personalInformation.ssn.error = 'Please enter a valid social security number'
        } else {
          this.personalInformation.ssn.error = ''
        }
      }
    },
    address: {
      handler(v) {
        if (this.notFoundClicked) {
          if (v.includes('PO') || v.includes('P.O.')) {
            this.personalInformation.addrStreetNumber.error =
              'This must be your residential address. P.O. boxes are not accepted.'
          } else {
            if (
              this.personalInformation.addrStreetNumber.error ===
              'This must be your residential address. P.O. boxes are not accepted.'
            ) {
              this.personalInformation.addrStreetNumber.error = ''
            }
            this.personalInformation.address.error = ''
          }
        } else {
          if (!v.includes(this.$store.state.applicationData.stateOfResidence.toUpperCase())) {
            this.personalInformation.address.error =
              'Your address must match the state you selected in the first step.'
          } else if (v.includes('PO') || v.includes('P.O.')) {
            this.personalInformation.address.error =
              'This must be your residential address. P.O. boxes are not accepted.'
          } else if (!v.match(/^\d/)) {
            this.personalInformation.address.error = 'Your address must include your street number.'
          } else {
            this.personalInformation.address.error = ''
          }
        }
      }
    },
    personalEmail: {
      handler(v) {
        if (v.length === 0) {
          this.personalInformation.home_phone_number.error = ''
        } else if (!v.includes('@') || !v.includes('.')) {
          this.personalInformation.personal_email.error =
            'Please enter a valid alternate email address'
        } else if (v.includes(' ')) {
          this.personalInformation.personal_email.error =
            'Please remove any spaces from this email address.'
        } else if (v === this.$store.state.user.signInUserSession.idToken.payload['email']) {
          this.personalInformation.personal_email.error =
            'Your alternate email address cannot match the email address used to create an account.'
        } else {
          this.personalInformation.personal_email.error = ''
        }
      }
    },
    homePhone: {
      handler(v) {
        if (v.length === 0) {
          this.personalInformation.home_phone_number.error = ''
        } else if (!v.match(/^\d{10}$/)) {
          this.personalInformation.home_phone_number.error = 'Please enter a valid phone number.'
        } else if (this.homePhone === this.phoneNumber) {
          this.personalInformation.home_phone_number.error =
            'Home phone cannot be the same as cell phone. Change home phone or leave blank.'
        } else {
          this.personalInformation.home_phone_number.error = ''
        }
      }
    },
    phoneNumber: {
      handler(newValue, oldValue) {
        if (oldValue !== '') {
          this.phoneChanged = true
        }
        if (!newValue.match(/^\d{10}$/)) {
          this.personalInformation.phone_number.error = 'Please enter a valid phone number.'
        } else if (this.homePhone === this.phoneNumber) {
          this.personalInformation.phone_number.error = ''
          this.personalInformation.home_phone_number.error =
            'Home phone cannot be the same as cell phone. Change home phone or leave blank.'
        } else {
          this.personalInformation.phone_number.error = ''
        }
      }
    },
    firstName: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.given_name.error = 'Please enter your first name.'
        } else {
          this.personalInformation.given_name.error = ''
        }
      }
    },
    lastName: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.family_name.error = 'Please enter your last name.'
        } else {
          this.personalInformation.family_name.error = ''
        }
      }
    },
    howYouHeard: {
      handler(v) {
        if (v) {
          if (v.length < 1) {
            this.personalInformation.how_you_heard.error = 'Please select how you heard about Viva.'
          } else {
            this.personalInformation.how_you_heard.error = ''
          }
        }
      }
    },
    postalCode: {
      handler(v) {
        if (v) {
          if (v.length !== 5) {
            this.personalInformation.addrPostalCode.error = 'Please enter the correct postal code.'
          } else {
            this.personalInformation.addrPostalCode.error = ''
          }
          this.personalInformation.address.value = `${this.personalInformation.addrStreetNumber.value} ${this.personalInformation.addrStreetName.value},${this.personalInformation.addrCity.value},${this.personalInformation.addrStateAbbr.value} ${this.personalInformation.addrPostalCode.value},USA`
        }
      }
    },
    state: {
      handler(v) {
        if (v) {
          if (v.length < 1) {
            this.personalInformation.addrStateAbbr.error = 'Please select a state.'
          } else if (
            !v.includes(this.$store.state.applicationData.stateOfResidence.toUpperCase())
          ) {
            this.personalInformation.addrStateAbbr.error =
              'Your address must match the state you selected in the first step.'
          } else {
            this.personalInformation.addrStateAbbr.error = ''
          }
          this.personalInformation.address.value = `${this.personalInformation.addrStreetNumber.value} ${this.personalInformation.addrStreetName.value},${this.personalInformation.addrCity.value},${this.personalInformation.addrStateAbbr.value} ${this.personalInformation.addrPostalCode.value},USA`
        }
      }
    },
    city: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.addrCity.error = 'Please enter city.'
        } else {
          this.personalInformation.addrCity.error = ''
        }
        this.personalInformation.address.value = `${this.personalInformation.addrStreetNumber.value} ${this.personalInformation.addrStreetName.value},${this.personalInformation.addrCity.value},${this.personalInformation.addrStateAbbr.value} ${this.personalInformation.addrPostalCode.value},USA`
      }
    },
    streetNum: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.addrStreetNumber.error = 'Please enter street number.'
        } else {
          this.personalInformation.addrStreetNumber.error = ''
        }
        this.personalInformation.address.value = `${this.personalInformation.addrStreetNumber.value} ${this.personalInformation.addrStreetName.value},${this.personalInformation.addrCity.value},${this.personalInformation.addrStateAbbr.value} ${this.personalInformation.addrPostalCode.value},USA`
      }
    },
    streetName: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.addrStreetName.error = 'Please enter street name.'
        } else {
          this.personalInformation.addrStreetName.error = ''
        }
        this.personalInformation.address.value = `${this.personalInformation.addrStreetNumber.value} ${this.personalInformation.addrStreetName.value},${this.personalInformation.addrCity.value},${this.personalInformation.addrStateAbbr.value} ${this.personalInformation.addrPostalCode.value},USA`
      }
    }
  }
}
</script>
